import { useEffect, useRef, useState } from 'react';

import Freeze from 'components/Streaks/Freeze';

import {
  BOUGHT_CAP,
  FREEZE_PRICE,
  freezesWord,
  money,
  paymentState,
  PaySource,
  PICKS,
  price,
  sourceName
} from './payment';

import styles from './BuyFreezesCard.module.scss';

export interface BuyFreezesCardProps {
  /** Bought freezes held (Promo and Cash); the cap of 3 counts only these. */
  bought: number;
  promo: number;
  cash: number;
  busy: boolean;
  error: string;
  /** Set once the purchase landed: how many, which balance paid, what is held after. */
  done: { quantity: number; source: PaySource; heldAfter: number; boughtAfter: number } | null;
  onBuy: (quantity: number, source: PaySource) => void;
  onAddFunds: (shortfall: number) => void;
  onClose: () => void;
}

/**
 * The house card for buying freezes (EN-11236, reset 10/27; canvas
 * BuyFreezesCard). $2 each, bought 1, 2 or 3 at a time, at most 3 bought held
 * at once; earned freezes sit outside the cap. One component for the phone
 * sheet and the tablet/desktop dialog. The server enforces the same cap.
 */
export default function BuyFreezesCard({
  bought,
  promo,
  cash,
  busy,
  error,
  done,
  onBuy,
  onAddFunds,
  onClose
}: BuyFreezesCardProps) {
  const room = Math.max(0, BOUGHT_CAP - Math.max(0, bought));
  const atCap = room === 0;
  const [pickedQty, setQty] = useState(1);
  const qty = Math.max(1, Math.min(room, pickedQty));
  const [picked, setPicked] = useState<PaySource | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const pay = paymentState(qty, promo, cash, picked);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (e: PointerEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMenuOpen(false);
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  if (done) {
    return (
      <div className={styles.card}>
        <div className={styles.done} role='status'>
          <Freeze size={72} />
          <h2 className={styles.doneTitle}>{freezesWord(done.quantity)} added.</h2>
          <span className={styles.doneBody}>
            You hold {freezesWord(done.heldAfter)} ({done.boughtAfter} bought), paid with{' '}
            {sourceName(done.source)}. The next day you miss, one is used by itself and your streak
            stays put.
          </span>
          <button
            type='button'
            className={`${styles.cta} ${styles.doneCta} ${styles.press}`}
            onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    );
  }

  const option = (key: PaySource, balance: number, covers: boolean) => {
    const selected = pay.source === key;
    return (
      <button
        key={key}
        type='button'
        role='option'
        aria-selected={selected}
        aria-disabled={!covers}
        className={`${styles.option} ${selected ? styles.optionOn : ''} ${
          covers ? '' : styles.optionOff
        }`}
        onClick={() => {
          if (!covers) return;
          setPicked(key);
          setMenuOpen(false);
        }}>
        <span className={styles.optionLabel}>
          <span
            className={`${styles.radio} ${selected ? styles.radioOn : ''}`}
            aria-hidden='true'
          />
          {sourceName(key)}
        </span>
        <span className={styles.optionAmount}>
          {covers ? money(balance) : `${money(balance)} · Not enough`}
        </span>
      </button>
    );
  };

  return (
    <div className={styles.card}>
      <div className={styles.head}>
        <div className={styles.titleBlock}>
          <h2 id='buy-freezes-title' className={styles.title}>
            Protect your streak.
          </h2>
          <span className={styles.lede}>
            A freeze covers one missed day by itself. {price(FREEZE_PRICE)} each, yours until used.
            Hold up to {BOUGHT_CAP} bought at a time; the ones you earn at milestones don&apos;t
            count.
          </span>
        </div>
        <button
          type='button'
          className={`${styles.close} ${styles.press}`}
          aria-label='Close'
          onClick={onClose}>
          <svg
            width='16'
            height='16'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2.2'
            strokeLinecap='round'
            aria-hidden='true'>
            <path d='M6 6l12 12M18 6L6 18' />
          </svg>
        </button>
      </div>

      <div className={styles.ticket}>
        <span className={styles.ticketDash} aria-hidden='true' />
        <div className={styles.rowBetween}>
          <span className={styles.howMany}>
            <Freeze size={40} />
            <span className={styles.typewriter}>How many</span>
          </span>
          <span className={styles.held}>
            Bought {Math.min(bought, BOUGHT_CAP)} of {BOUGHT_CAP}
          </span>
        </div>

        {atCap ? (
          <p className={styles.capNote}>
            You hold {BOUGHT_CAP} bought freezes, the most at a time. Once one covers a day, you can
            buy again.
          </p>
        ) : (
          <div className={styles.picks} role='radiogroup' aria-label='How many freezes'>
            {PICKS.map((n) => {
              const fits = n <= room;
              const cost = price(n * FREEZE_PRICE);
              return (
                <button
                  key={n}
                  type='button'
                  role='radio'
                  aria-checked={n === qty}
                  aria-disabled={!fits}
                  aria-label={`${freezesWord(n)} for ${cost}${
                    fits ? '' : `, over the limit of ${BOUGHT_CAP} bought`
                  }`}
                  className={`${styles.pick} ${styles.press} ${n === qty ? styles.pickOn : ''} ${
                    fits ? '' : styles.pickOff
                  }`}
                  onClick={() => {
                    if (!fits) return;
                    setQty(n);
                    setMenuOpen(false);
                  }}>
                  <span className={styles.pickLabel}>{freezesWord(n)}</span>
                  <span className={styles.pickPrice}>{cost}</span>
                </button>
              );
            })}
          </div>
        )}

        <div className={styles.sep} aria-hidden='true' />
        <div className={styles.summary}>
          <span className={styles.typewriter} style={{ fontSize: 12, color: '#2b170a' }}>
            {atCap ? 'Nothing to buy today' : `${freezesWord(qty)} × ${price(FREEZE_PRICE)}`}
          </span>
          {!atCap && <span className={styles.total}>{price(pay.total)}</span>}
        </div>

        {!atCap &&
          (pay.source ? (
            <div className={styles.payLine} ref={menuRef}>
              <div className={styles.payText}>
                <span>Pay with</span>
                <button
                  type='button'
                  className={styles.source}
                  aria-haspopup='listbox'
                  aria-expanded={menuOpen}
                  onClick={() => setMenuOpen((o) => !o)}>
                  {sourceName(pay.source)}
                  <svg
                    width='10'
                    height='10'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='3'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    aria-hidden='true'>
                    <path d='M6 9l6 6 6-6' />
                  </svg>
                </button>
                <span>· {money(pay.source === 'cash' ? cash : promo)} available</span>
              </div>
              {menuOpen && (
                <div className={styles.menu} role='listbox' aria-label='Pay with'>
                  {option('promo', promo, pay.promoCovers)}
                  {option('cash', cash, pay.cashCovers)}
                </div>
              )}
            </div>
          ) : (
            <>
              <span className={styles.short}>
                {money(pay.shortfall)} short · Promo {money(promo)} · Cash {money(cash)}
              </span>
              <button
                type='button'
                className={styles.addFunds}
                onClick={() => onAddFunds(pay.shortfall)}>
                Add funds
              </button>
            </>
          ))}
      </div>

      {atCap ? (
        <button type='button' className={styles.cta} disabled aria-disabled='true'>
          At the limit · {BOUGHT_CAP} bought
        </button>
      ) : (
        <button
          type='button'
          className={`${styles.cta} ${styles.press}`}
          disabled={!pay.source || busy}
          aria-disabled={!pay.source || busy}
          onClick={() => pay.source && onBuy(qty, pay.source)}>
          {busy ? 'Buying…' : `Buy ${freezesWord(qty)} · ${price(pay.total)}`}
        </button>
      )}
      {error && (
        <p className={styles.error} role='alert'>
          {error}
        </p>
      )}
      <button type='button' className={`${styles.secondary} ${styles.press}`} onClick={onClose}>
        Not now
      </button>
      <p className={styles.fine}>
        Non-refundable. A freeze covers a day you miss; it doesn&apos;t add a day. Up to{' '}
        {BOUGHT_CAP} bought at a time; earned ones don&apos;t count toward it.
      </p>
    </div>
  );
}
